import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {
    IonButton,
    IonButtons,
    IonCheckbox,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonList,
    IonSearchbar,
    IonTitle,
    IonToolbar
} from '@ionic/angular/standalone';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
    selector: 'app-searchable-select',
    templateUrl: './searchable-select.component.html',
    styleUrls: ['./searchable-select.component.css'],
    imports: [
        IonButton,
        IonButtons,
        IonCheckbox,
        IonContent,
        IonHeader,
        IonItem,
        IonList,
        IonSearchbar,
        IonTitle,
        IonToolbar,
        IonIcon,
        TranslatePipe
    ],
    standalone: true,
})
export class SearchableSelectComponent<T> implements OnInit {
    @Input({required: true}) titleKey!: string;
    @Input({required: true}) items: T[] = [];
    @Input({required: true}) form!: FormGroup;
    @Input({required: true}) itemsControlName!: string;
    @Input({required: true}) searchableProperty!: keyof T;
    @Input({required: true}) trackProperty!: keyof T;
    @Input({required: true}) compareItemsMethod!: (firstItem: T, secondItem: T) => boolean;
    @Input() fallbackSearchableProperty?: keyof T;
    @Input() maxSelectedItems?: number;
    @Input() warningThreshold?: number;

    @Output() selectionCompleted = new EventEmitter<void>();

    public filteredItems: T[] = [];
    public selectedItems: T[] = [];

    public get amountOfItemsBeforeExceedingLimit(): number {
        return this.maxSelectedItems ? this.maxSelectedItems - this.selectedItems.length : 0;
    }

    ngOnInit() {
        this.filteredItems = [...this.items];
        this.selectedItems = this.form.get(this.itemsControlName)?.value ?? [];
    }

    public cancelChanges() {
        this.selectionCompleted.emit();
    }

    public confirmChanges() {
        this.form.patchValue({
            [this.itemsControlName]: this.selectedItems
        });
        this.selectionCompleted.emit();
    }

    public searchbarInput(event: Event) {
        const inputElement = event.target as HTMLInputElement;
        this.filterList(inputElement.value);
    }

    public isChecked(value: T): boolean {
        return this.selectedItems.some(item => this.compareItemsMethod(item, value));
    }

    public checkboxChange(event: CustomEvent<{ checked: boolean; value: T }>) {
        const {checked, value} = event.detail;
        if (checked) {
            this.selectedItems = [...this.selectedItems, value];
        } else {
            this.selectedItems = this.selectedItems.filter((item) => !this.compareItemsMethod(item, value));
        }
    }

    public selectAllItems() {
        this.selectedItems = this.filteredItems;
    }

    public deselectAllItems() {
        this.selectedItems = [];
    }

    private filterList(searchQuery: string | undefined) {
        if (searchQuery === undefined || searchQuery.trim().length == 0) {
            this.filteredItems = [...this.items];
        } else {
            const lowercaseQuery = searchQuery.toLowerCase();
            this.filteredItems = this.items.filter((item) => {
                const value = item[this.searchableProperty]?.toString().toLowerCase();
                const fallbackValue = this.fallbackSearchableProperty
                    ? item[this.fallbackSearchableProperty]?.toString().toLowerCase() : undefined;
                return value ? value.includes(lowercaseQuery) : fallbackValue?.includes(lowercaseQuery);
            });
        }
    }
}
